import * as React from "react"
import { cn } from "@/lib/utils"

interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  onCheckedChange?: (checked: boolean) => void
  size?: "sm" | "md" | "lg" | "xl"
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, onCheckedChange, checked, size = "lg", ...props }, ref) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onCheckedChange?.(event.target.checked)
    }

    const handleTrackClick = () => {
      onCheckedChange?.(!checked)
    }

    // Size configurations
    const sizeConfig = {
      sm: {
        track: "h-4 w-7",
        thumb: "h-3 w-3",
        translate: "translate-x-3",
        top: "top-0.5"
      },
      md: {
        track: "h-5 w-9",
        thumb: "h-4 w-4",
        translate: "translate-x-4",
        top: "top-0.5"
      },
      lg: {
        track: "h-6 w-11",
        thumb: "h-5 w-5",
        translate: "translate-x-5",
        top: "top-0.5"
      },
      xl: {
        track: "h-8 w-14",
        thumb: "h-7 w-7",
        translate: "translate-x-6",
        top: "top-0.5"
      }
    }

    const config = sizeConfig[size]

    return (
      <div className="relative">
        <input
          type="checkbox"
          className="sr-only"
          ref={ref}
          checked={checked}
          onChange={handleChange}
          {...props}
        />
        <div
          onClick={handleTrackClick}
          className={cn(
            "relative cursor-pointer rounded-full transition-colors duration-200 ease-in-out",
            config.track,
            checked ? "bg-blue-600" : "bg-gray-200",
            "focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <div
            className={cn(
              "absolute rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out",
              config.top,
              config.thumb,
              checked ? config.translate : "translate-x-0"
            )}
          />
        </div>
      </div>
    )
  }
)
Switch.displayName = "Switch"

export { Switch } 